/** @prettier */

import {useEffect, useState} from 'react';
import {Box, ToggleButtonGroup, ToggleButton, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';

//Style import
import '../css/common/common.css';

import {initialUpper} from '../functions/convertString';
import {iconBlack, iconWhite} from '../define';

/**
 * 指令画面　風量指令ボタン群
 * @param props props
 * - displayFlow 画面に表示する風量
 * - setOpeFlow 風量の指令値設定関数
 * @returns JSX
 */
const AirflowButtonGroup = (props: {
  displayFlow: 'auto' | 'low' | 'middle' | 'high' | 'powerful' | undefined;
  setOpeFlow: React.Dispatch<
    React.SetStateAction<
      'auto' | 'low' | 'middle' | 'high' | 'powerful' | undefined
    >
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const {displayFlow, setOpeFlow} = props;

  const [flow, setFlow] = useState<string | undefined>(props.displayFlow); //風量設定　指令値

  /** 風量ラベル文字列変換 */
  const airFlowLabelSource: {[key: string]: string} = {
    'Auto': trans('AirflowButtonGroup.fanSpeed.auto'),
    'Low': trans('AirflowButtonGroup.fanSpeed.low'),
    'Middle': trans('AirflowButtonGroup.fanSpeed.medium'),
    'High': trans('AirflowButtonGroup.fanSpeed.high'),
    'Powerful': trans('AirflowButtonGroup.fanSpeed.powerful'),
    '': '',
  };

  useEffect(() => {
    if (displayFlow !== undefined) {
      setFlow(initialUpper(displayFlow));
    }
    setOpeFlow(displayFlow);
  }, [displayFlow, setOpeFlow]);

  /**
   * 風量設定ボタン押下時処理
   * @param {React.MouseEvent<HTMLElement>} event マウスイベント
   * @param {string | null} newAlignment  設定値？
   */
  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    if (newAlignment !== null) {
      setFlow(initialUpper(newAlignment));

      const temp = newAlignment.toLowerCase();
      if (
        temp === 'auto' ||
        temp === 'low' ||
        temp === 'middle' ||
        temp === 'high' ||
        temp === 'powerful'
      ) {
        props.setOpeFlow(temp);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <Box
        id="mv-txtOperationAirFlow"
        className="operation__card__content__setting__value__currentValue">
        {flow === undefined ? '' : airFlowLabelSource[flow]}
      </Box>
      <ToggleButtonGroup
        value={flow}
        exclusive
        onChange={handleAlignment}
        aria-label="Fan speed change"
        id="mv-btnSelectOperationAirFlow"
        className="operation__card__content__setting__value__toggleButton">
        <ToggleButton
          title={`${trans('AirflowButtonGroup.image.title.auto')}`}
          value="Auto"
          aria-label="auto"
          id="mv-btnSelectOperationAirFlowAuto"
          className={
            flow === 'Auto'
              ? 'operation__card__content__setting__value__toggleButton--fan--selected'
              : ''
          }>
          <img
            className="operation__card__content__setting__value__toggleButton__image"
            src={
              flow === 'Auto'
                ? iconWhite.FAN_SPEED_AUTO.src
                : iconBlack.FAN_SPEED_AUTO.src
            }
            alt={
              flow === 'Auto'
                ? `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconWhite.FAN_SPEED_AUTO.alt}`
                  )}`
                : `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconBlack.FAN_SPEED_AUTO.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('AirflowButtonGroup.image.title.low')}`}
          value="Low"
          aria-label="low"
          id="mv-btnSelectOperationAirFlowLow"
          className={
            flow === 'Low'
              ? 'operation__card__content__setting__value__toggleButton--fan--selected'
              : ''
          }>
          <img
            className="operation__card__content__setting__value__toggleButton__image"
            src={
              flow === 'Low'
                ? iconWhite.FAN_SPEED_1.src
                : iconBlack.FAN_SPEED_1.src
            }
            alt={
              flow === 'Low'
                ? `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconWhite.FAN_SPEED_1.alt}`
                  )}`
                : `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconBlack.FAN_SPEED_1.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('AirflowButtonGroup.image.title.medium')}`}
          value="Middle"
          aria-label="Medium"
          id="mv-btnSelectOperationAirFlowMiddle"
          className={
            flow === 'Middle'
              ? 'operation__card__content__setting__value__toggleButton--fan--selected'
              : ''
          }>
          <img
            className="operation__card__content__setting__value__toggleButton__image"
            src={
              flow === 'Middle'
                ? iconWhite.FAN_SPEED_2.src
                : iconBlack.FAN_SPEED_2.src
            }
            alt={
              flow === 'Middle'
                ? `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconWhite.FAN_SPEED_2.alt}`
                  )}`
                : `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconBlack.FAN_SPEED_2.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('AirflowButtonGroup.image.title.high')}`}
          value="High"
          aria-label="high"
          id="mv-btnSelectOperationAirFlowHigh"
          className={
            flow === 'High'
              ? 'operation__card__content__setting__value__toggleButton--fan--selected'
              : ''
          }>
          <img
            className="operation__card__content__setting__value__toggleButton__image"
            src={
              flow === 'High'
                ? iconWhite.FAN_SPEED_3.src
                : iconBlack.FAN_SPEED_3.src
            }
            alt={
              flow === 'High'
                ? `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconWhite.FAN_SPEED_3.alt}`
                  )}`
                : `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconBlack.FAN_SPEED_3.alt}`
                  )}`
            }
          />
        </ToggleButton>
        <ToggleButton
          title={`${trans('AirflowButtonGroup.image.title.powerful')}`}
          value="Powerful"
          aria-label="powerful"
          id="mv-btnSelectOperationAirFlowPowerful"
          className={
            flow === 'Powerful'
              ? 'operation__card__content__setting__value__toggleButton--fan--selected'
              : ''
          }>
          <img
            className="operation__card__content__setting__value__toggleButton__image"
            src={
              flow === 'Powerful'
                ? iconWhite.FAN_SPEED_4.src
                : iconBlack.FAN_SPEED_4.src
            }
            alt={
              flow === 'Powerful'
                ? `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconWhite.FAN_SPEED_4.alt}`
                  )}`
                : `${trans(
                    `AirflowButtonGroup.image.alternativeText.${iconBlack.FAN_SPEED_4.alt}`
                  )}`
            }
          />
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};
export default AirflowButtonGroup;
